import { gql } from 'graphql-request';
import type { CurrencyISO } from '@/constants/currencies';
import type { ISO2 } from '@/constants/countries';
import { request } from '@utilities/graphQLClient';
import type { QueryFunctionContext } from '@tanstack/react-query';
import type { PreferredMultiple } from './selector';
import type { GraphQLError } from 'graphql';

export function getHistogramQueryKey(
  canonicalURL?: string,
  multiple?: PreferredMultiple
): [string, string, string | undefined] {
  return ['COMPANY_INDUSTRY_HISTOGRAM', canonicalURL || '', multiple];
}

export function getPeersQueryKey(
  canonicalURL?: string,
  limit?: number,
  customPeers?: string[]
): [string, string, number, string[]] {
  return ['COMPANY_PEERS', canonicalURL || '', limit || 4, customPeers || []];
}

export interface CompanyIndustryHistogramBucket {
  count: number;
  key: string;
  from: number | null;
  to: number | null;
}

interface CompanyIndustryHistogram {
  bucketSize: number;
  countryISO: string;
  level: string;
  maxRange: number;
  median: number;
  minRange: number;
  preferredMultiple: string;
  secondaryIndustryId: number;
  region: string;
  count: number;
  buckets: CompanyIndustryHistogramBucket[];
}

export interface Company {
  id: string;
  name: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  exchangeCountryISO: ISO2;
  listingCurrencyISO: CurrencyISO;
  isPrimaryListing: boolean;
  analysisValue: {
    marketCap: number | null;
    marketCapUSD: number | null;
    pe: number | null;
    pb: number | null;
    priceToSales: number | null;
    return1yAbs: number | null;
  };
  analysisFuture: {
    netIncome1Y: number | null;
    revenue1Y: number | null;
    netIncomeGrowth1Y: number | null;
    revenueGrowth1Y: number | null;
    netIncomeGrowthAnnual: number | null;
    revenueGrowthAnnual: number | null;
    forwardPe1Y: number | null;
    forwardPriceToSales1Y: number | null;
  };
  country: {
    iso2: string;
    region: {
      id: string;
      name: string;
    };
  };
  score: {
    value: number | null;
    future: number | null;
    past: number | null;
    health: number | null;
    dividend: number | null;
  };
}

interface CompanyPeer {
  companyId: string;
  score: number;
  company: Company;
}

interface Error {
  message: string;
}
export type CustomPeer = {
  id: string; //actually is the uniqueSymbol
  name: string;
};
interface CompanyPeersResponse extends Company {
  peers: CompanyPeer[];
}

interface CompanyHistogramReponse {
  industryHistogram: CompanyIndustryHistogram;
}

export interface QueryPeersResponse {
  Company: CompanyPeersResponse;
  x;
  errors?: Error[];
  customPeers: CustomPeer[];
}

interface QueryHistogramResponse {
  Company: CompanyHistogramReponse;
  errors?: Error[];
}

function getCustomPeersData(response: QueryPeersResponse) {
  return response.Company.peers.map((peer) => ({
    id: peer.company.uniqueSymbol,
    name: peer.company.name,
  }));
}
const companyFields = gql`
  fragment CompanyFields on Company {
    id
    name
    tickerSymbol
    uniqueSymbol
    canonicalURL
    exchangeCountryISO
    listingCurrencyISO
    isPrimaryListing
    analysisValue {
      marketCap
      marketCapUSD
      pe
      pb
      priceToSales
      return1yAbs
    }
    analysisFuture {
      netIncome1Y
      revenue1Y
      netIncomeGrowth1Y
      revenueGrowth1Y
      forwardPe1Y
      forwardPriceToSales1Y
      netIncomeGrowthAnnual
      revenueGrowthAnnual
    }
    country {
      iso2
      region {
        id
        name
      }
    }
    score {
      value
      future
      past
      health
      dividend
    }
  }
`;

export const queryPeersFn = async ({
  queryKey: [_keyof, canonicalURL, limit, customPeers],
}: QueryFunctionContext<
  [string, string, number, string[]]
>): Promise<QueryPeersResponse> => {
  try {
    const response = await request<QueryPeersResponse>(
      gql`
        ${companyFields}
        query getCompanyPeers(
          $canonicalURL: String
          $limit: Float
          $customPeers: [String!]
        ) {
          Company(canonicalURL: $canonicalURL) {
            ...CompanyFields
            peers(limit: $limit, customPeers: $customPeers) {
              companyId
              score
              company {
                ...CompanyFields
              }
            }
          }
        }
      `,
      {
        canonicalURL,
        limit,
        customPeers: customPeers.filter(Boolean),
      }
    );
    const { errors } = response;
    if (errors && errors.length > 0) {
      console.log(errors[0].message);
      throw new Error(errors[0].message);
    }
    const customPeersData = getCustomPeersData(response);
    return { ...response, customPeers: customPeersData };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const queryHistogramOptions = (canonicalUrl: string | undefined) => {
  return {
    staleTime: Infinity,
    enabled: Boolean(canonicalUrl),
    retryDelay: 4000,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const graphQlErrors: GraphQLError[] | undefined = error?.response?.errors;
      const unprocessable =
        graphQlErrors &&
        graphQlErrors.length &&
        graphQlErrors.some((err) => err.extensions?.code === '422');
      if (unprocessable || failureCount > 2) {
        return false;
      }
      return true;
    },
  };
};

export const queryHistogramFn = async ({
  queryKey: [_keyof, canonicalURL, multiple],
}: QueryFunctionContext<
  [string, string, string | undefined]
>): Promise<QueryHistogramResponse> => {
  const response = await request<QueryHistogramResponse>(
    gql`
      query getCompanyHistogram($canonicalURL: String, $multiple: String) {
        Company(canonicalURL: $canonicalURL) {
          industryHistogram(preferredMultiple: $multiple) {
            bucketSize
            countryISO
            level
            maxRange
            median
            minRange
            preferredMultiple
            secondaryIndustryId
            region
            count
            buckets {
              count
              key
              from
              to
            }
          }
        }
      }
    `,
    {
      canonicalURL,
      multiple: multiple?.toLowerCase(),
    }
  );
  const { errors } = response;
  if (errors && errors.length > 0) {
    console.log(errors[0].message);
    throw new Error(errors[0].message);
  }
  return response;
};
